import { useQuery } from "@tanstack/react-query";
import type { DockFreeWindows, WarehouseClock } from "@wms/domain";
import { AutoComplete, DatePicker, Form, Space, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import "./dateTime.css";
import { busyAt, deviceZone, insideWork, matchesTyped, parseTime, timeOptions, workWords, zoneOffset } from "./dateTime";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Quick times of a planned document; those outside the working hours of the warehouse are not offered. */
const QUICK_TIMES = [
  { key: "morning", label: "Утро", time: "09:00" },
  { key: "day", label: "День", time: "13:00" },
  { key: "evening", label: "Вечер", time: "17:00" },
];

interface Props {
  value?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
  id?: string;
  disabled?: boolean;
  /** The warehouse whose clock and working hours the time is typed in (its card is read when `clock` is not given). */
  warehouseId?: string | null;
  /** The clock itself, when the caller already has it (the client's cabinet). */
  clock?: WarehouseClock | null;
  /** Offer the free windows of the gates of the warehouse on the chosen day (a receipt, an order, a dock slot). */
  docks?: boolean;
  /** A planned time: no «Сейчас», the day starts with the quick times of a shift. */
  planning?: boolean;
}

/**
 * «Удобный выбор времени» (04.11.2026): a date and a time as two plain fields instead of one picker with two unnamed
 * columns of 24 and 60 numbers. The time is typed as «14», «1430» or «14:30», picked from a list every 30 minutes
 * inside the working hours of the warehouse, or set by «Утро / День / Вечер». The line under the field names the clock
 * (the warehouse's zone; the device's own time too when it differs), warns — never refuses — about a time outside the
 * working hours or when every gate is taken, and offers the free windows of the gates for a receipt or an order.
 * The value stays one instant (`Dayjs`) for the form; the API receives it in UTC as before.
 */
export function DateTimeField({ value, onChange, id, disabled, warehouseId, clock, docks = false, planning = false }: Props) {
  const card = useQuery({
    queryKey: ["warehouses", warehouseId],
    queryFn: () => api.get<WarehouseClock>(`/warehouses/${warehouseId}`),
    enabled: !!warehouseId && !clock,
    staleTime: 300_000,
  });
  const warehouse: WarehouseClock | null = clock ?? card.data ?? null;
  const zone = warehouse?.timezone || deviceZone();
  const ofWarehouse = !!warehouse?.timezone;

  // the date and the time on the clock of `zone`, kept apart so that half of a value can be typed
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const [bad, setBad] = useState(false);
  useEffect(() => {
    if (!value) {
      setDate(null);
      setTime(null);
      setTyped("");
      return;
    }
    const local = dayjs(value).tz(zone);
    setDate(local.format("YYYY-MM-DD"));
    setTime(local.format("HH:mm"));
    setTyped(local.format("HH:mm"));
    setBad(false);
  }, [value, zone]);

  const from = warehouse?.work_from ?? null;
  const to = warehouse?.work_to ?? null;
  const options = useMemo(() => timeOptions(from, to), [from, to]);
  const quick = QUICK_TIMES.filter((q) => insideWork(q.time, from, to));
  const firstTime = quick[0]?.time ?? options[0] ?? "09:00";

  const windows = useQuery({
    queryKey: ["dock-free-windows", warehouseId, date],
    queryFn: () => api.get<DockFreeWindows>("/dock-slots/free-windows", { warehouse_id: warehouseId, date }),
    enabled: docks && !!warehouseId && !!date,
    retry: false, // no right to the hint (403) — the field works without it
    staleTime: 60_000,
  });
  const busy = windows.data?.busy ?? [];

  const emit = (nextDate: string | null, nextTime: string | null) => {
    if (!nextDate) return onChange?.(null);
    // a day without a time gets the first time of the shift, shown in the time field — never a hidden 00:00
    const clockTime = nextTime ?? firstTime;
    onChange?.(dayjs.tz(`${nextDate}T${clockTime}`, zone));
  };
  const today = dayjs().tz(zone);
  const pickTime = (clockTime: string) => {
    setTyped(clockTime);
    setBad(false);
    emit(date ?? today.format("YYYY-MM-DD"), clockTime);
  };
  const commitTyped = () => {
    if (!typed.trim()) {
      setBad(false);
      if (date) emit(date, null);
      return;
    }
    const parsed = parseTime(typed);
    if (!parsed) return setBad(true);
    pickTime(parsed);
  };

  const outside = time && !insideWork(time, from, to) ? workWords(from, to) : null;
  const taken = time && busy.length ? busyAt(time, busy) : null;
  const offset = zoneOffset(zone, value ? dayjs(value).toDate() : new Date());
  const mine = deviceZone();
  const myOffset = zoneOffset(mine, value ? dayjs(value).toDate() : new Date());
  const differs = ofWarehouse && myOffset !== offset;
  const listed = options
    .filter((o) => matchesTyped(o, typed === time ? "" : typed))
    .map((o) => ({
      value: o,
      label: busyAt(o, busy) ? (
        <span>
          {o} <Typography.Text type="secondary">· ворота заняты</Typography.Text>
        </span>
      ) : (
        o
      ),
    }));

  return (
    <div className="wms-datetime" data-testid="datetime-field">
      <Space.Compact style={{ width: "100%" }}>
        <DatePicker
          id={id}
          value={date ? dayjs(date) : null}
          format="DD.MM.YYYY"
          placeholder="Дата"
          aria-label="Дата"
          disabled={disabled}
          style={{ flex: "1 1 60%" }}
          onChange={(day) => emit(day ? day.format("YYYY-MM-DD") : null, day ? time : null)}
          data-testid="datetime-date"
        />
        <AutoComplete
          value={typed}
          options={listed}
          disabled={disabled}
          status={bad ? "error" : undefined}
          style={{ flex: "1 1 40%", minWidth: 110 }}
          placeholder="Время, 14:30"
          aria-label="Время"
          id={id ? `${id}-time` : undefined}
          onChange={(text) => {
            setTyped(text);
            setBad(false);
          }}
          onSelect={(picked: string) => pickTime(picked)}
          onBlur={commitTyped}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitTyped();
            }
          }}
          data-testid="datetime-time"
        />
      </Space.Compact>
      {!disabled ? (
        <Space size={[4, 4]} wrap style={{ marginTop: 6 }}>
          <Tag className="wms-datetime-chip" onClick={() => emit(today.format("YYYY-MM-DD"), time)} data-testid="datetime-chip-today">
            Сегодня
          </Tag>
          <Tag className="wms-datetime-chip" onClick={() => emit(today.add(1, "day").format("YYYY-MM-DD"), time)} data-testid="datetime-chip-tomorrow">
            Завтра
          </Tag>
          {quick.map((q) => (
            <Tag key={q.key} className="wms-datetime-chip" color={time === q.time ? "blue" : undefined} onClick={() => pickTime(q.time)} data-testid={`datetime-chip-${q.key}`}>
              {q.label} {q.time}
            </Tag>
          ))}
          {!planning ? (
            <Tag className="wms-datetime-chip" onClick={() => onChange?.(dayjs().tz(zone).second(0).millisecond(0))} data-testid="datetime-chip-now">
              Сейчас
            </Tag>
          ) : null}
        </Space>
      ) : null}
      {bad ? (
        <Typography.Text type="danger" style={{ display: "block", fontSize: 13 }} data-testid="datetime-error">
          «{typed}» не похоже на время. Введите, например, 14:30 или 1430
        </Typography.Text>
      ) : null}
      <Typography.Text type="secondary" style={{ display: "block", fontSize: 13, marginTop: 2 }} data-testid="datetime-zone">
        {ofWarehouse
          ? `Время склада${warehouse?.code ? ` ${warehouse.code}` : ""}: ${zone}, ${offset}`
          : `Время этого устройства: ${zone}, ${offset}`}
        {workWords(from, to) ? ` · работает ${workWords(from, to)}` : ""}
        {differs && value ? ` · по вашим часам ${dayjs(value).tz(mine).format("DD.MM HH:mm")} (${myOffset})` : ""}
      </Typography.Text>
      {outside ? (
        <Typography.Text type="warning" style={{ display: "block", fontSize: 13 }} data-testid="datetime-warning">
          {time} — вне рабочих часов склада ({outside}). Время сохранится; проверьте, что оно верное.
        </Typography.Text>
      ) : null}
      {taken ? (
        <Typography.Text type="warning" style={{ display: "block", fontSize: 13 }} data-testid="datetime-warning">
          В {time} все ворота заняты ({taken.from}–{taken.to}). Выберите свободное окно или проверьте расписание доков.
        </Typography.Text>
      ) : null}
      {docks && windows.data && windows.data.gates > 0 && !disabled ? (
        <div style={{ marginTop: 4 }} data-testid="datetime-docks">
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {windows.data.free.length ? "Свободно у ворот: " : "На этот день все ворота заняты в рабочие часы"}
          </Typography.Text>
          {windows.data.free.map((w) => (
            <Tag key={w.from} className="wms-datetime-chip" color="green" onClick={() => pickTime(w.from)} data-testid="datetime-window">
              {w.from}–{w.to}
            </Tag>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The field inside a meta form (EntityForm): the clock follows the warehouse chosen in the same form; the planned time
 * of a receipt, an order or a dock slot (`widget: "dock_time"`) also offers the free windows of the gates.
 */
export function FormDateTimeField({ name, widget, ...rest }: Props & { name: string; widget?: string | null }) {
  const form = Form.useFormInstance();
  const warehouseId = Form.useWatch("warehouse_id", form) as string | undefined;
  const planning = /^(planned|expected)/.test(name);
  return <DateTimeField {...rest} warehouseId={warehouseId ?? null} docks={widget === "dock_time"} planning={planning} />;
}
